import { useCallback, useEffect, useMemo, useState } from "react"

import { formatNumber } from "accounting"
import { Col, Flex, Row, Typography } from "antd"
import cls from "classname"
import ReactCountryFlag from "react-country-flag"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import Globe from "../../../Components/Globe"
import Private from "../../../Components/Private"
import SearchComponent from "../../../Components/Search"
import TableComponent from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
import DeflectFilters from "../components/DeflectFilters"
import { withValueSkeleton } from "../components/Skeleton/cardSkeleton"
import { sortableTitle } from "../components/sortableTitle"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import { useDeflectScope } from "../hooks/useDeflectScope"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { countryName } from "../utils/records"

import "../../DashboardPage/styles.less"
import tables from "../tables.module.less"

const { Title, Text } = Typography

// Rows the table's skeleton shows before the first answer: the Top countries card's ten.
const SKELETON_ROWS = 10

// Country breakdown table, one-for-one with Prevent's alertsByCountryColumns: country cell
// with a flag, the numeric columns fixed-width and right-aligned, money masked by Incognito,
// and a Share column carrying each country's percentage of the total.
//
// Every column sorts: the page exists to answer "which is the busiest one", and that question
// changes column by column. The default order is lookups descending, as the data arrives.
const countryColumns = (t) => [
  {
    title: sortableTitle(t("Country"), "label"),
    dataIndex: "label",
    key: "label",
    sorter: (a, b) => countryName(a.key).localeCompare(countryName(b.key)),
    render: (_label, record) => (
      <Flex align="center" gap={8} className="country-cell">
        {record.key && (
          <ReactCountryFlag
            countryCode={record.key}
            svg
            style={{ width: 20, height: 15, borderRadius: 4, flexShrink: 0 }}
            aria-hidden
          />
        )}
        <Text ellipsis={{ tooltip: countryName(record.key) }}>
          {countryName(record.key) || t("N/A")}
        </Text>
      </Flex>
    )
  },
  {
    title: sortableTitle(t("Lookups"), "count", { align: "right" }),
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    defaultSortOrder: "descend",
    sorter: (a, b) => a.count - b.count,
    render: (_) => <Private>{formatNumber(_)}</Private>
  },
  {
    title: sortableTitle(t("Volume ($)"), "value", { align: "right" }),
    dataIndex: "value",
    key: "value",
    width: 160,
    align: "right",
    sorter: (a, b) => a.value - b.value,
    render: (_) => <Private>{`$${formatNumber(_, 2, ",")}`}</Private>
  },
  {
    title: sortableTitle(t("Average volume"), "average", { align: "right" }),
    dataIndex: "average",
    key: "average",
    width: 180,
    align: "right",
    sorter: (a, b) => (a.average || 0) - (b.average || 0),
    render: (_) => <Private>{`$${formatNumber(_ ?? 0, 2, ",")}`}</Private>
  },
  {
    title: sortableTitle(t("Share"), "percentage", { align: "right" }),
    dataIndex: "percentage",
    key: "percentage",
    width: 110,
    align: "right",
    sorter: (a, b) => (a.percentage || 0) - (b.percentage || 0),
    render: (_) => <Private>{`${formatNumber(_ ?? 0, 1, ",", ".")}%`}</Private>
  }
]

// The summary's cells under the numeric columns: lookups, volume, average, share.
const SUMMARY_CELLS = [
  { align: "right", render: (s) => <Private>{formatNumber(s.lookups_count)}</Private> },
  { align: "right", render: (s) => <Private>{`$${formatNumber(s.fees_amount, 2, ",")}`}</Private> },
  {
    align: "right",
    render: (s) => (
      <Private>{`$${formatNumber(s.lookups_count ? s.fees_amount / s.lookups_count : 0, 2, ",")}`}</Private>
    )
  },
  null
]

// The lookups-by-country breakdown, ported from Prevent's "Alerts by Country" page: back link,
// header + calendar, the shaded globe with four summary cards, then a searchable country table,
// out of the same by-country/ answer the Top countries card reads: every country, sorted and
// searched here. Volume is what the lookups were charged.
const CountriesFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getCompare } = useDeflectScope()
  const byCountry = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_COUNTRY))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_COUNTRY))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_COUNTRY))
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)

  useEffect(() => {
    getCompare(DEFLECT_SECTION.BY_COUNTRY)
  }, [getCompare])

  const summary = byCountry?.summary

  const tableRows = useMemo(
    () =>
      (byCountry?.result ?? []).map((row) => ({
        key: row.country_code,
        label: countryName(row.country_code),
        count: row.lookups_count,
        value: row.fees_amount,
        average: row.avg_fee,
        percentage: row.share
      })),
    [byCountry]
  )

  // The globe keys countries by ISO alpha-2 and shades them by lookup count; the tooltip reads
  // the same count plus the volume charged.
  const mapData = useMemo(
    () =>
      tableRows.map((r) => ({
        code: r.key,
        name: r.label,
        // `value` is what the globe shades by; count and volume are what its tooltip reads.
        value: r.count,
        count: r.count,
        volume: r.value
      })),
    [tableRows]
  )

  // Prevent's Breakdown by Country paints a band behind each row, scaled against the busiest
  // country rather than the total, so the widest row is always full width and the ranking reads
  // without looking at the numbers. Sorting by another column re-scales nothing: the band always
  // means lookups, which is what the rows are ranked on by default.
  const maxCount = useMemo(() => Math.max(0, ...tableRows.map((r) => r.count || 0)), [tableRows])
  const rowBarProps = useCallback(
    (record) => ({
      style: { "--dfl-row-bar": `${maxCount ? ((record.count || 0) / maxCount) * 100 : 0}%` }
    }),
    [maxCount]
  )

  const [countrySearch, setCountrySearch] = useState("")
  const filteredRows = useMemo(() => {
    const term = countrySearch.trim().toLowerCase()
    if (!term) return tableRows
    return tableRows.filter((r) => countryName(r.key).toLowerCase().includes(term))
  }, [tableRows, countrySearch])

  const cards = useMemo(
    () => [
      { title: "Total lookups", value: summary?.lookups_count ?? 0, icon: "receipt_check" },
      { title: "Total volume", value: summary?.fees_amount ?? 0, prefix: "$", icon: "money" },
      { title: "Countries", value: summary?.countries_count ?? 0, icon: "globe" },
      {
        title: "Top country",
        value: summary?.top_country_code ? countryName(summary.top_country_code) : "-",
        icon: "pin"
      }
    ],
    [summary]
  )

  const columns = useMemo(() => countryColumns(t), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])

  useEffect(() => {
    document.title = "Lookups by Country - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.deflect.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col
        span={24}
        style={
          isMobile
            ? { width: "100%", maxWidth: "unset", flex: "unset", padding: "unset" }
            : undefined
        }
      >
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Lookups by Country")}
            </Title>
            <Flex align="center" gap={8}>
              <span className="live-dot" aria-hidden="true" />
              <Text type="secondary">{t("Updates every few minutes")}</Text>
            </Flex>
          </div>

          <DeflectFilters />
        </Flex>
      </Col>

      <Col span={24}>
        <div className="globe-hero">
          <Globe
            data={mapData}
            theme={theme}
            isMobile={isMobile}
            ariaLabel={t("Lookups by Country")}
            legendAriaLabel={t("Darker means more lookups")}
            renderTooltipValue={(row) => (
              <>
                <Private>{formatNumber(row.count, 0, ",", ".")}</Private> {t("lookups")} &middot;{" "}
                <Private>{formatCurrency(row.volume ?? 0, true)}</Private>
              </>
            )}
          />

          <Row gutter={[24, 24]} className="globe-cards">
            {(firstLoad ? withValueSkeleton(cards) : cards).map((card) => (
              <Col key={card.title} xs={24} sm={12} lg={6}>
                <DashboardCard data={card} theme={theme} />
              </Col>
            ))}
          </Row>
        </div>
      </Col>

      <Col span={24}>
        <Flex vertical gap={16} className="breakdown-table-header">
          <Title level={5} style={{ marginBottom: 0 }}>
            {t("Country breakdown")}
          </Title>
          <div className="breakdown-search">
            <SearchComponent
              handleSearch={(e) => setCountrySearch(e.target.value)}
              value={countrySearch}
              placeholder={t("Search country")}
            />
          </div>
        </Flex>

        <TableComponent
          className={cls(tables.olympics, tables.rowBar)}
          dataSource={firstLoad ? skeletonRows(SKELETON_ROWS) : filteredRows}
          columns={firstLoad ? loadingColumns : columns}
          onRow={firstLoad ? undefined : rowBarProps}
          loading={fetching && !firstLoad}
          withOutPagination
          scroll={{}}
          rowKey="key"
          tableLayout="fixed"
          showHeader={firstLoad || !!filteredRows.length}
          summary={() => (
            <SummaryRows
              summary={filteredRows.length && !firstLoad ? summary : null}
              comparisonSummary={byCountry?.comparison_summary}
              comparisonLabel={byCountry?.comparison_period?.label}
              cells={SUMMARY_CELLS}
            />
          )}
          locale={{
            emptyText: countrySearch ? (
              <EmptyComponent type="search" style={{ minHeight: 450 }} />
            ) : (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            )
          }}
        />
      </Col>
    </Row>
  )
}

export default CountriesFullPage
